import * as React from 'react'
import { formatDistanceStrict } from 'date-fns'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Checkbox from '@mui/joy/Checkbox'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import DialogActions from '@mui/joy/DialogActions'
import DialogContent from '@mui/joy/DialogContent'
import DialogTitle from '@mui/joy/DialogTitle'
import FormControl from '@mui/joy/FormControl'
import FormHelperText from '@mui/joy/FormHelperText'
import FormLabel from '@mui/joy/FormLabel'
import IconButton from '@mui/joy/IconButton'
import Input from '@mui/joy/Input'
import Modal from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import ModalDialog from '@mui/joy/ModalDialog'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Switch from '@mui/joy/Switch'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Tooltip from '@mui/joy/Tooltip'
import Typography from '@mui/joy/Typography'
import { Add, Check, ContentCopy, Refresh } from '@mui/icons-material'
import {
    DocumentDeviceJobRecord,
    DocumentJobStatus,
    DocumentDeviceRecord,
    DocumentDevicesResponse,
    DocumentEnrollmentCreated,
    DocumentEnrollmentRecord,
    DocumentEnrollmentStatus,
} from '@numeo/types-core'
import { formatToUserLocalTime } from '@/utils/date-formatting'
import { apiErrorMessage, documentDevicesApi } from './api'
import { AttemptTags } from './AttemptTags'
import { STATUS_HINTS } from '../document-pipeline-audit/hints'
import { DeviceState, deviceState, visibleDevices } from './devices'
import { deviceOutcome, isProblem, otherTries, STATUS_LABEL, summarize } from './deviceJobs'

/** How often the lists refresh on their own, so a laptop that finishes installing appears without a reload. */
const REFRESH_MS = 15_000

const STATE_LABEL: Record<DeviceState, string> = {
    [DeviceState.ONLINE]: 'Online',
    [DeviceState.OFFLINE]: 'Offline',
    [DeviceState.WAITING]: 'Waiting for first connection',
    [DeviceState.REVOKED]: 'Revoked',
}
const STATE_COLOR: Record<DeviceState, 'success' | 'neutral' | 'warning' | 'danger'> = {
    [DeviceState.ONLINE]: 'success',
    [DeviceState.OFFLINE]: 'neutral',
    [DeviceState.WAITING]: 'warning',
    [DeviceState.REVOKED]: 'danger',
}
const ENROLLMENT_COLOR: Record<DocumentEnrollmentStatus, 'primary' | 'success' | 'neutral'> = {
    [DocumentEnrollmentStatus.PENDING]: 'primary',
    [DocumentEnrollmentStatus.REDEEMED]: 'success',
    [DocumentEnrollmentStatus.EXPIRED]: 'neutral',
    [DocumentEnrollmentStatus.CANCELLED]: 'neutral',
}

const LIFETIMES: Array<{ minutes: number; label: string }> = [
    { minutes: 15, label: '15 minutes' },
    { minutes: 30, label: '30 minutes' },
    { minutes: 60, label: '1 hour' },
    { minutes: 240, label: '4 hours' },
]

const JOB_STATUS_COLOR: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
    [DocumentJobStatus.LLM_DONE]: 'success',
    [DocumentJobStatus.LLM_NOT_OF_INTEREST]: 'neutral',
    [DocumentJobStatus.LLM_COMBINED]: 'warning',
    [DocumentJobStatus.OCR_FAILED]: 'danger',
    [DocumentJobStatus.LLM_FAILED]: 'danger',
    [DocumentJobStatus.OCR_LEASED]: 'warning',
}

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } as const

/** The command without the code, for an operator who would rather the laptop ask for it than have it in shell history. */
const withoutCode = (installCommand: string): string => installCommand.split(' -s -- ')[0]

/** The jobs a device claimed, one row each: what the device did, where the job is now, and the other workers' tries on hover. */
function DeviceJobsTable({
    jobs,
    deviceNames,
    onlyProblems,
    setOnlyProblems,
    now,
}: {
    jobs: DocumentDeviceJobRecord[]
    deviceNames: string[]
    onlyProblems: boolean
    setOnlyProblems: (value: boolean) => void
    now: number
}) {
    const summary = summarize(jobs, deviceNames)
    const shown = onlyProblems ? jobs.filter((j) => isProblem(j, deviceNames)) : jobs
    const truncate = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const
    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="sm" variant="soft">
                        {summary.total} jobs
                    </Chip>
                    <Chip size="sm" variant="soft" color="success">
                        {summary.read} read
                    </Chip>
                    <Chip size="sm" variant="soft" color={summary.notDelivered ? 'warning' : 'neutral'}>
                        {summary.notDelivered} did not deliver
                    </Chip>
                    {summary.inProgress > 0 && (
                        <Chip size="sm" variant="soft">
                            {summary.inProgress} in progress
                        </Chip>
                    )}
                    {summary.unrecorded > 0 && (
                        <Tooltip title="Claimed by this device, but no OCR try of its own is on record (for example, another reader finished the job).">
                            <Chip size="sm" variant="soft">
                                {summary.unrecorded} with no record
                            </Chip>
                        </Tooltip>
                    )}
                </Box>
                <Switch checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} endDecorator="Only problems" />
            </Box>
            {shown.length === 0 ? (
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    No problems in these jobs.
                </Typography>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                    <Table
                        stickyHeader
                        size="sm"
                        sx={{ tableLayout: 'fixed', minWidth: 760, '--TableCell-headBackground': 'var(--joy-palette-background-level1)' }}
                    >
                        <thead>
                            <tr>
                                <th style={{ width: '14%' }}>Claimed</th>
                                <th style={{ width: '30%' }}>Document</th>
                                <th style={{ width: '28%' }}>This device</th>
                                <th style={{ width: '28%' }}>Now</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((j) => {
                                const outcome = deviceOutcome(j, deviceNames)
                                const others = otherTries(j, deviceNames)
                                return (
                                    <tr key={j.jobId}>
                                        <td>
                                            <Tooltip title={`${formatToUserLocalTime(j.claimedAt)} · job ${j.jobId}`}>
                                                <span>{ago(j.claimedAt, now)}</span>
                                            </Tooltip>
                                        </td>
                                        <td>
                                            <Tooltip title={[j.filename, j.projectName].filter(Boolean).join(' · ')}>
                                                <Box>
                                                    <Typography level="body-sm" sx={truncate}>
                                                        {j.filename ?? '--'}
                                                    </Typography>
                                                    <Typography level="body-xs" sx={{ color: 'text.tertiary', ...truncate }}>
                                                        {[j.projectName, j.pageCount ? `${j.pageCount} page${j.pageCount === 1 ? '' : 's'}` : null]
                                                            .filter(Boolean)
                                                            .join(' · ')}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        </td>
                                        <td>
                                            <Typography
                                                level="body-sm"
                                                color={outcome.tone === 'success' ? 'success' : outcome.tone === 'warning' ? 'warning' : 'neutral'}
                                            >
                                                {outcome.text}
                                            </Typography>
                                            {outcome.detail && (
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary', ...truncate }} title={outcome.detail}>
                                                    {outcome.detail}
                                                </Typography>
                                            )}
                                            {others.length > 0 && (
                                                <Tooltip title={<AttemptTags attempts={others} />} variant="outlined">
                                                    <Typography level="body-xs" sx={{ color: 'text.tertiary', cursor: 'help', width: 'fit-content' }}>
                                                        +{others.length} other {others.length === 1 ? 'try' : 'tries'}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </td>
                                        <td>
                                            <Tooltip title={[STATUS_HINTS[j.status], j.lastError, `(${j.status})`].filter(Boolean).join(' · ')}>
                                                <Chip size="sm" variant="soft" color={JOB_STATUS_COLOR[j.status] ?? 'neutral'}>
                                                    {STATUS_LABEL[j.status] ?? j.status}
                                                </Chip>
                                            </Tooltip>
                                            {j.resolvedDocType && (
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.25 }}>
                                                    {j.resolvedDocType}
                                                </Typography>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </Sheet>
            )}
        </>
    )
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
    const [copied, setCopied] = React.useState(false)
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // Clipboard access can be refused (insecure context, permissions): the text is on screen to select by hand.
        }
    }
    return (
        <Button size="sm" variant="soft" color={copied ? 'success' : 'neutral'} startDecorator={copied ? <Check /> : <ContentCopy />} onClick={copy}>
            {copied ? 'Copied' : label}
        </Button>
    )
}

/**
 * Registered devices and how to add one (ADR-022.4). An operator names a laptop and gets a one-time code plus the single
 * command to paste into that laptop's Terminal; the laptop then installs itself and appears here. Keys can be revoked.
 */
const ago = (iso: string, now: number) => formatDistanceStrict(new Date(iso), now, { addSuffix: true })

export default function DocumentDevicesPage() {
    const [data, setData] = React.useState<DocumentDevicesResponse | null>(null)
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState<string | null>(null)
    const [refreshTick, setRefreshTick] = React.useState(0)

    const [addOpen, setAddOpen] = React.useState(false)
    const [label, setLabel] = React.useState('')
    const [ttlMinutes, setTtlMinutes] = React.useState(30)
    const [creating, setCreating] = React.useState(false)
    const [createError, setCreateError] = React.useState<string | null>(null)
    const [created, setCreated] = React.useState<DocumentEnrollmentCreated | null>(null)

    const [revokeTarget, setRevokeTarget] = React.useState<DocumentDeviceRecord | null>(null)
    const [revoking, setRevoking] = React.useState(false)
    const [actionError, setActionError] = React.useState<string | null>(null)
    const [cancellingId, setCancellingId] = React.useState<string | null>(null)
    const [showRevoked, setShowRevoked] = React.useState(false)
    const [selected, setSelected] = React.useState<DocumentDeviceRecord | null>(null)
    const [jobs, setJobs] = React.useState<DocumentDeviceJobRecord[] | null>(null)
    const [jobsError, setJobsError] = React.useState<string | null>(null)
    /** Which device's jobs were asked for last: a slower answer for an earlier device is dropped. */
    const jobsRequest = React.useRef(0)

    React.useEffect(() => {
        let cancelled = false
        documentDevicesApi
            .getOverview()
            .then((overview) => {
                if (cancelled) return
                setData(overview)
                setError(null)
            })
            .catch((e) => !cancelled && setError(apiErrorMessage(e, 'Failed to load devices')))
            .finally(() => !cancelled && setLoading(false))
        return () => {
            cancelled = true
        }
    }, [refreshTick])

    React.useEffect(() => {
        const timer = setInterval(() => setRefreshTick((n) => n + 1), REFRESH_MS)
        return () => clearInterval(timer)
    }, [])

    const refresh = () => setRefreshTick((n) => n + 1)

    const closeAdd = () => {
        setAddOpen(false)
        setCreated(null)
        setLabel('')
        setCreateError(null)
        refresh()
    }

    const createCode = async () => {
        setCreating(true)
        setCreateError(null)
        try {
            setCreated(await documentDevicesApi.createEnrollment(label.trim(), ttlMinutes))
            refresh()
        } catch (e) {
            setCreateError(apiErrorMessage(e, 'Failed to create the code'))
        } finally {
            setCreating(false)
        }
    }

    const confirmRevoke = async () => {
        if (!revokeTarget) return
        setRevoking(true)
        setActionError(null)
        try {
            await documentDevicesApi.revokeKey(revokeTarget.keyId)
            setRevokeTarget(null)
            refresh()
        } catch (e) {
            setActionError(apiErrorMessage(e, 'Failed to revoke the key'))
            setRevokeTarget(null)
        } finally {
            setRevoking(false)
        }
    }

    const cancelCode = async (codeId: string) => {
        setCancellingId(codeId)
        setActionError(null)
        try {
            await documentDevicesApi.cancelEnrollment(codeId)
            refresh()
        } catch (e) {
            setActionError(apiErrorMessage(e, 'Failed to cancel the code'))
        } finally {
            setCancellingId(null)
        }
    }

    const [onlyProblems, setOnlyProblems] = React.useState(false)

    const openDevice = async (device: DocumentDeviceRecord) => {
        const request = ++jobsRequest.current
        setOnlyProblems(false)
        setSelected(device)
        setJobs(null)
        setJobsError(null)
        try {
            const loaded = await documentDevicesApi.getDeviceJobs(device.keyId)
            if (request === jobsRequest.current) setJobs(loaded)
        } catch (e) {
            if (request === jobsRequest.current) setJobsError(apiErrorMessage(e, 'Failed to load the jobs of this device'))
        }
    }

    const closeDevice = () => {
        jobsRequest.current++
        setSelected(null)
    }

    const now = Date.now()
    const allDevices = data?.devices ?? []
    const revokedCount = allDevices.filter((d) => d.revokedAt).length
    const devices = visibleDevices(allDevices, showRevoked)
    const enrollments = data?.enrollments ?? []
    const createdEnrollment = created ? enrollments.find((e) => e.codeId === created.codeId) : undefined
    const createdRedeemed = createdEnrollment?.status === DocumentEnrollmentStatus.REDEEMED
    const labelValid = /^[\p{L}\p{N} ._()@'’-]{1,100}$/u.test(label.trim())

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography level="h3">Devices &amp; Keys</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary', maxWidth: 720 }}>
                        Laptops that read documents for the pipeline. Add one with a one-time code and a single command; each laptop gets its own key, which you
                        can revoke here.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton variant="outlined" aria-label="Refresh" onClick={refresh} disabled={loading}>
                        <Refresh />
                    </IconButton>
                    <Button startDecorator={<Add />} onClick={() => setAddOpen(true)}>
                        Add a laptop
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            )}
            {actionError && (
                <Alert color="danger" variant="soft">
                    {actionError}
                </Alert>
            )}

            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography level="title-md">Registered devices</Typography>
                    {revokedCount > 0 && (
                        <Checkbox size="sm" label={`Show revoked (${revokedCount})`} checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} />
                    )}
                </Box>
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                    <Table stickyHeader hoverRow sx={{ '--TableCell-headBackground': 'var(--joy-palette-background-level1)', '--TableCell-paddingY': '8px' }}>
                        <thead>
                            <tr>
                                <th>Device</th>
                                <th style={{ width: 190 }}>Status</th>
                                <th>Platform</th>
                                <th>Reads with</th>
                                <th>Last seen</th>
                                <th>Added</th>
                                <th style={{ width: 96 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && !data && (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>
                                        <CircularProgress size="sm" />
                                    </td>
                                </tr>
                            )}
                            {data && devices.length === 0 && (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>
                                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                            {allDevices.length === 0
                                                ? 'No devices yet. Click “Add a laptop” to enrol the first one.'
                                                : 'Every device is revoked. Tick “Show revoked” to see them.'}
                                        </Typography>
                                    </td>
                                </tr>
                            )}
                            {devices.map((d) => {
                                const state = deviceState(d, now)
                                return (
                                    <tr key={d.keyId} onClick={() => void openDevice(d)} style={{ cursor: 'pointer' }}>
                                        <td>
                                            <Typography level="title-sm">{d.label}</Typography>
                                            <Typography level="body-xs" sx={{ color: 'text.tertiary', ...mono }}>
                                                {d.keyId.slice(0, 8)}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Chip size="sm" variant="soft" color={STATE_COLOR[state]}>
                                                {STATE_LABEL[state]}
                                            </Chip>
                                        </td>
                                        <td>
                                            {d.device ? (
                                                <>
                                                    <Typography level="body-sm">{d.device.platform}</Typography>
                                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                        worker {d.device.workerVersion}
                                                    </Typography>
                                                </>
                                            ) : (
                                                '--'
                                            )}
                                        </td>
                                        <td>
                                            {d.device && d.device.adapters.length > 0 ? (
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {d.device.adapters.map((a) => (
                                                        <Chip key={a} size="sm" variant="outlined">
                                                            {a}
                                                        </Chip>
                                                    ))}
                                                </Box>
                                            ) : (
                                                '--'
                                            )}
                                        </td>
                                        <td>
                                            {d.device ? (
                                                <Tooltip title={formatToUserLocalTime(d.device.lastSeenAt)}>
                                                    <span>{ago(d.device.lastSeenAt, now)}</span>
                                                </Tooltip>
                                            ) : (
                                                '--'
                                            )}
                                        </td>
                                        <td>
                                            <Tooltip title={formatToUserLocalTime(d.createdAt)}>
                                                <span>{ago(d.createdAt, now)}</span>
                                            </Tooltip>
                                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                {d.createdBy?.startsWith('enrollment:') ? 'by code' : (d.createdBy ?? '')}
                                            </Typography>
                                        </td>
                                        <td>
                                            {state !== DeviceState.REVOKED && (
                                                <Button
                                                    size="sm"
                                                    variant="plain"
                                                    color="danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setRevokeTarget(d)
                                                    }}
                                                >
                                                    Revoke
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </Sheet>
            </Box>

            <Box>
                <Typography level="title-md" sx={{ mb: 1 }}>
                    Enrollment codes
                </Typography>
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                    <Table hoverRow sx={{ '--TableCell-headBackground': 'var(--joy-palette-background-level1)', '--TableCell-paddingY': '8px' }}>
                        <thead>
                            <tr>
                                <th>For</th>
                                <th style={{ width: 130 }}>Status</th>
                                <th>Created</th>
                                <th>Expires</th>
                                <th>Used by</th>
                                <th style={{ width: 96 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {data && enrollments.length === 0 && (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: 'center', padding: 16 }}>
                                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                            No codes yet.
                                        </Typography>
                                    </td>
                                </tr>
                            )}
                            {enrollments.map((e: DocumentEnrollmentRecord) => (
                                <tr key={e.codeId}>
                                    <td>
                                        <Typography level="title-sm">{e.label}</Typography>
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                            by {e.createdBy}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Chip size="sm" variant="soft" color={ENROLLMENT_COLOR[e.status]}>
                                            {e.status}
                                        </Chip>
                                    </td>
                                    <td>{ago(e.createdAt, now)}</td>
                                    <td>{e.status === DocumentEnrollmentStatus.PENDING ? ago(e.expiresAt, now) : '--'}</td>
                                    <td>
                                        {e.redeemedDevice ? (
                                            <>
                                                <Typography level="body-sm">{e.redeemedDevice}</Typography>
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary', ...mono }}>
                                                    {e.redeemedIp}
                                                </Typography>
                                            </>
                                        ) : (
                                            '--'
                                        )}
                                    </td>
                                    <td>
                                        {e.status === DocumentEnrollmentStatus.PENDING && (
                                            <Button
                                                size="sm"
                                                variant="plain"
                                                color="neutral"
                                                loading={cancellingId === e.codeId}
                                                onClick={() => cancelCode(e.codeId)}
                                            >
                                                Cancel
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            </Box>

            <Modal open={!!selected} onClose={closeDevice}>
                <ModalDialog sx={{ width: 'min(980px, 96vw)', maxHeight: '90vh', overflow: 'auto' }}>
                    <ModalClose />
                    <DialogTitle>{selected?.label}</DialogTitle>
                    <DialogContent sx={{ gap: 1.5 }}>
                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                            The last 50 jobs this device claimed in the last 60 days, newest first. “This device” is what it did with the job; “Now” is where
                            the job is today.
                        </Typography>
                        {jobsError && (
                            <Alert color="danger" variant="soft">
                                {jobsError}
                            </Alert>
                        )}
                        {!jobs && !jobsError && <CircularProgress size="sm" />}
                        {jobs && jobs.length === 0 && (
                            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                This device has not claimed any job yet.
                            </Typography>
                        )}
                        {jobs && jobs.length > 0 && selected && (
                            <DeviceJobsTable
                                jobs={jobs}
                                deviceNames={[selected.label, selected.device?.deviceId ?? ''].filter(Boolean)}
                                onlyProblems={onlyProblems}
                                setOnlyProblems={setOnlyProblems}
                                now={now}
                            />
                        )}
                    </DialogContent>
                </ModalDialog>
            </Modal>

            <Modal open={addOpen} onClose={closeAdd}>
                <ModalDialog sx={{ width: 'min(680px, 94vw)' }}>
                    <DialogTitle>{created ? 'Install on the laptop' : 'Add a laptop'}</DialogTitle>
                    {!created ? (
                        <>
                            <DialogContent sx={{ gap: 2 }}>
                                <Typography level="body-sm">Name the laptop and choose how long its one-time code stays valid. The code works once.</Typography>
                                <FormControl error={label.length > 0 && !labelValid}>
                                    <FormLabel>Name</FormLabel>
                                    <Input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Beka's MacBook Air" />
                                    <FormHelperText>Letters, numbers, spaces and . _ ( ) @ ' - only. It is how the laptop appears in the list.</FormHelperText>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Code is valid for</FormLabel>
                                    <Select value={ttlMinutes} onChange={(_, value) => value && setTtlMinutes(value)}>
                                        {LIFETIMES.map((l) => (
                                            <Option key={l.minutes} value={l.minutes}>
                                                {l.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </FormControl>
                                {createError && (
                                    <Alert color="danger" variant="soft">
                                        {createError}
                                    </Alert>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={createCode} loading={creating} disabled={!labelValid}>
                                    Create code
                                </Button>
                                <Button variant="plain" color="neutral" onClick={closeAdd}>
                                    Cancel
                                </Button>
                            </DialogActions>
                        </>
                    ) : (
                        <>
                            <DialogContent sx={{ gap: 2 }}>
                                {createdRedeemed ? (
                                    <Alert color="success" variant="soft" startDecorator={<Check />}>
                                        Enrolled. “{createdEnrollment?.label}” used the code from {createdEnrollment?.redeemedDevice}. It appears in the device
                                        list as soon as it connects.
                                    </Alert>
                                ) : (
                                    <>
                                        <Typography level="body-sm">
                                            On the laptop, open <b>Terminal</b>, paste this command and press Enter. It downloads the Numeo device package, asks
                                            for the Mac’s password once, installs everything and starts it. The code expires {ago(created.expiresAt, now)}.
                                        </Typography>
                                        <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                            <Typography level="body-sm" sx={{ ...mono, wordBreak: 'break-all', flex: 1 }}>
                                                {created.installCommand}
                                            </Typography>
                                            <CopyButton text={created.installCommand} />
                                        </Sheet>
                                        <Alert color="warning" variant="soft">
                                            This is the only time the code is shown; it is not stored. If you lose it, cancel it and create another.
                                        </Alert>
                                        <details>
                                            <summary style={{ cursor: 'pointer' }}>
                                                <Typography level="body-sm" component="span">
                                                    Rather keep the code out of the laptop’s shell history?
                                                </Typography>
                                            </summary>
                                            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <Typography level="body-sm">
                                                    Run this instead; it asks for the code, which you paste when prompted (nothing is echoed).
                                                </Typography>
                                                <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                    <Typography level="body-sm" sx={{ ...mono, wordBreak: 'break-all', flex: 1 }}>
                                                        {withoutCode(created.installCommand)}
                                                    </Typography>
                                                    <CopyButton text={withoutCode(created.installCommand)} />
                                                </Sheet>
                                                <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    <Typography level="body-sm" sx={{ ...mono, wordBreak: 'break-all', flex: 1 }}>
                                                        {created.code}
                                                    </Typography>
                                                    <CopyButton text={created.code} label="Copy code" />
                                                </Sheet>
                                            </Box>
                                        </details>
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                            Needs an Apple Silicon Mac (M1 or newer) on macOS 14 or later. Waiting for the laptop to use the code…
                                        </Typography>
                                    </>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={closeAdd}>{createdRedeemed ? 'Done' : 'Close'}</Button>
                            </DialogActions>
                        </>
                    )}
                </ModalDialog>
            </Modal>

            <Modal open={!!revokeTarget} onClose={() => !revoking && setRevokeTarget(null)}>
                <ModalDialog variant="outlined" role="alertdialog">
                    <DialogTitle>Revoke “{revokeTarget?.label}”?</DialogTitle>
                    <DialogContent>
                        <Typography level="body-sm">
                            Its key stops working immediately, so this device can no longer claim documents. Jobs it is reading are handed to another worker
                            when their lease ends. To use the laptop again, add it with a new code.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button color="danger" loading={revoking} onClick={confirmRevoke}>
                            Revoke key
                        </Button>
                        <Button variant="plain" color="neutral" disabled={revoking} onClick={() => setRevokeTarget(null)}>
                            Keep it
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </Box>
    )
}
